import { useState, useEffect, useRef } from "react";
import { ArrowLeft } from 'lucide-react';
import { useNotification } from "../../../components/Notifications/NotificationContext";
import { authService } from "../../../services/api";
import { MediaManager } from "../../../components/MediaManager/MediaManager";
import PlanManager from "../../../components/PlanManager/PlanManager";
import PersonalizedOrderBump from "../OrderBump/PersonalizedOrderBump";
import TextForm from '../../../components/TextForm/TextForm';
import { validateFields } from "../../../utils/contentBlacklistValidator";
import { deliveriesFromApi } from "../../../utils/apiPlansToForm";
import { useBotCurrency } from "../../../hooks/useBotCurrency";
import { useOnlyUpsellBlocked } from "../../../hooks/useOnlyUpsellBlocked";

export default function UpsellForm({ botId, editing, onEditingChange, onSuccess, onBack }) {
    const { showError, showSuccess } = useNotification();
    // Espelha a moeda-base do bot: planos do upsell mostram $ quando o bot é USD.
    const { currencyMode, baseCurrency } = useBotCurrency(botId);
    const onlyUpsellBlocked = useOnlyUpsellBlocked(botId);
    const mediaRef = useRef();
    const bumpRef = useRef();

    const [form, setForm] = useState({
        id: null,
        name: "",
        message: "",
        trigger_after: "all",
        delay_minutes: 0,
        show_this_upsell_delivery: true,
        delivery: null,
        pix_values: [],
        plan_names: [],
        plan_durations: [],
        plan_color: [],
        plan_deliveries: [],
    });

    const [initialPlans, setInitialPlans] = useState(null);

    const [mediaList, setMediaList] = useState([]);
    const [sendMode, setSendMode] = useState('agrupado');

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (editing) {
            setForm({
                id: editing.id || null,
                name: editing.name || "",
                message: editing.message || "",
                trigger_after: editing.trigger_after || "all",
                position_in_chain: editing.position_in_chain || 1,
                delay_minutes: editing.delay_minutes || 0,
                show_this_upsell_delivery: editing.show_this_upsell_delivery ?? true,
                delivery: editing.delivery || null,
                pix_values: editing.pix_values || [],
                plan_names: editing.plan_names || [],
                plan_durations: editing.plan_durations || [],
                plan_color: editing.plan_color || editing.plan_colors || [],
                plan_deliveries: deliveriesFromApi(editing),
            });

            // Passar dados iniciais para o PlanManager
            setInitialPlans({
                names: editing.plan_names || [],
                values: editing.pix_values || [],
                durations: editing.plan_durations || [],
                color: editing.plan_color || editing.plan_colors || [],
                deliveries: deliveriesFromApi(editing),
            });

            setSendMode(editing.media_send_mode || 'agrupado');

            if (editing.media_list && editing.media_list.length > 0) {
                setMediaList(editing.media_list);
            } else {
                const legacy = [];
                if (editing.image_file_id) legacy.push({ media_type: 'photo', file_id: editing.image_file_id, thumbnail_file_id: null });
                if (editing.video_file_id) legacy.push({ media_type: 'video', file_id: editing.video_file_id, thumbnail_file_id: null });
                if (editing.audio_file_id) legacy.push({ media_type: 'voice', file_id: editing.audio_file_id, thumbnail_file_id: null });
                setMediaList(legacy);
            }
        } else {
            resetForm();
        }
    }, [editing]);

    // Receber atualização de planos do PlanManager (RESPONSABILIDADE DELEGADA AO COMPONENTE)
    const handlePlansUpdate = (updatedPlans) => {
        setForm(prev => ({
            ...prev,
            plan_names: updatedPlans.names,
            pix_values: updatedPlans.values,
            plan_durations: updatedPlans.durations,
            plan_color: updatedPlans.color,
            plan_deliveries: updatedPlans.deliveries || [],
        }));
    };

    const resetForm = () => {
        setForm({
            id: null,
            name: "",
            message: "",
            trigger_after: "all",
            delay_minutes: 0,
            show_this_upsell_delivery: true,
            delivery: null,
            pix_values: [],
            plan_names: [],
            plan_durations: [],
            plan_color: [],
            plan_deliveries: [],
        });
        setInitialPlans(null);
        setMediaList([]);
        setSendMode('agrupado');
        onEditingChange(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const contentCheck = validateFields([
            { label: 'Nome do Upsell', value: form.name },
            { label: 'Mensagem do Upsell', value: form.message },
            ...(form.plan_names || []).map((name, index) => ({
                label: `Plano ${index + 1} - Nome`,
                value: name,
            })),
        ]);
        if (!contentCheck.isValid) {
            showError(contentCheck.error);
            return;
        }

        // Valida o Order Bump ANTES de criar o upsell (se inválido, nem cria a oferta).
        const bumpErr = bumpRef.current?.validate?.();
        if (bumpErr) {
            showError(bumpErr);
            return;
        }

        setLoading(true);

        try {
            // 1️⃣ Salvar upsell
            const { data } = await authService.upsell.save_upsell(botId, { ...form, media_send_mode: sendMode });

            if (!data.success) throw new Error(data.error);

            // 2️⃣ Pegar o ID (criar ou editar)
            const upsellId = data.offer_id || form.id;

            // 3️⃣ Upload da mídia do upsell
            if (mediaRef.current && upsellId) {
                await mediaRef.current.uploadAll(upsellId);
            }

            // 4️⃣ Order bump personalizado — salvo JUNTO, já com o upsell criado.
            try {
                await bumpRef.current?.save?.(upsellId);
            } catch (e) {
                showError(e?.response?.data?.message || e.message || "Erro ao salvar Order Bump");
                return;
            }

            showSuccess(form.id ? "Upsell atualizado com sucesso!" : "Upsell criado com sucesso!");
            resetForm();
            onSuccess();
        } catch (err) {
            showError(err?.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <header style={{ marginBottom: 24 }}>
                <div class="flex items-center gap-3">
                    <button onClick={onBack} type="button" class="p-2 rounded-lg bg-white/[0.02] border border-white/[0.06] text-white/50 hover:text-white hover:bg-white/[0.04] transition-colors cursor-pointer"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-left" aria-hidden="true"><path d="m12 19-7-7 7-7"></path><path d="M19 12H5"></path></svg></button>
                    <div>
                        <h1 className='text-2xl font-bold text-white'>{form.id ? "Editar Upsell" : "Novo Upsell"}</h1>
                        <p class="text-sm text-white/40 mt-0.5">Configure uma oferta de upsell na sua cadeia de vendas</p>
                    </div>
                </div>
            </header>

            <form onSubmit={handleSubmit} style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}>

                {/* Nome */}
                <div className="mt-4">
                    <label className="spt-label">Nome do Upsell <span className="required">*</span></label>
                    <input
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        required
                        className="input3"
                        placeholder="Ex: Upsell 1 - Acesso Premium"
                    />
                    <small className="form-hint">
                        Nome do upsell para identificação interna. Não é mostrado para os usuários.
                    </small>
                </div>

                {/* MEDIA */}
                <div className="mt-8">
                    <MediaManager
                        ref={mediaRef}
                        botId={botId}
                        targetType="offer"
                        targetId={form.id}
                        multiMedia={true}
                        mediaList={mediaList}
                        onMediaChange={setMediaList}
                        sendMode={sendMode}
                        onSendModeChange={setSendMode}
                        uploadContext="upsell"
                    />
                </div>

                <div className="mt-4">
                    <label className="spt-label">Mensagem do Upsell <span className="required">*</span></label>
                    <TextForm value={form.message} onChange={(value) => setForm({ ...form, message: value })} hint="Mensagem que será exibida ao cliente, apresentando a oferta adicional." showVariables={false} />
                </div>

                {/* Configuração da Chain */}
                <div className="mt-8">
                    <div className="flex gap-8 mx-auto justify-center align-center">
                        <div className="w-full">
                            <label className="spt-label">Delay (min) <span className='required'>*</span></label>
                            <select
                                className="form-select"
                                value={form.delay_minutes}
                                onChange={(e) => setForm({
                                    ...form,
                                    delay_minutes: parseInt(e.target.value)
                                })}
                            >
                                <option value={0}>Imediato</option>
                                <option value={5}>5 minutos</option>
                                <option value={10}>10 minutos</option>
                                <option value={15}>15 minutos</option>
                                <option value={20}>20 minutos</option>
                                <option value={30}>30 minutos</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* PLANOS - RESPONSABILIDADE DELEGADA AO PlanManager */}
                <PlanManager
                    initialPlans={initialPlans}
                    onUpdate={handlePlansUpdate}
                    label="Planos de Pagamento"
                    currencyMode={currencyMode}
                    baseCurrency={baseCurrency}
                    enableDelivery={true}
                    defaultDeliveryLabel="Usar Entrega Global dos Upsells"
                    onlyUpsellBlocked={onlyUpsellBlocked}
                />

                {/* Order bump personalizado — configurado desde a criação;
                    salvo junto com o upsell (botão "Salvar Upsell" abaixo). */}
                <div className="mt-8">
                    <PersonalizedOrderBump
                        ref={bumpRef}
                        botId={botId}
                        offerId={form.id}
                        uploadContext="upsell_orderbump"
                    />
                </div>

                {/* BOTÕES */}
                <div className="flex gap-4 mt-6 justify-end">
                    <button type="button" onClick={onBack} className="btn-secondary">
                        <ArrowLeft size={16} className="inline" />Voltar
                    </button>
                    <button disabled={loading} className="btn-primary">
                        {loading ? "Salvando..." : "Salvar Upsell"}
                    </button>
                </div>

            </form>
        </>
    );
}
